import { Injectable } from '@angular/core';
import * as blinder from 'color-blind';
import { COLOR_NAMES, HEX_CODES } from '../../data/color/color.data';
import { RAL_CLASSIC, RalColor } from '../../data/color/ral.data';
import { GRADIENT_NAMES } from '../../data/gradient/gradient-name.data';
import { ChartData } from '../../model/chart/chart-data.model';

// A nearest RAL Classic match: the RAL entry plus the perceptual distance
// (CIE76 deltaE in Lab) between it and the queried colour.
export interface RalMatch extends RalColor {
  deltaE: number;
}

@Injectable( {
  providedIn: 'root',
} )
export class ColorService {

  constructor() {
    // Intentionally blank
  }

  // Generators

  generateRandomHexColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for ( let i = 0; i < 6; i++ ) {
      color += letters[ Math.floor( Math.random() * 16 ) ];
    }
    return color;
  }

  generateLessSaturatedColor( hex: string, amount: number ): string {
    const hsl = this.hexToHsl( hex.replace( '#', '' ) );
    const lessSaturatedHsl = {
      h: hsl.h,
      s: Math.max( 0, hsl.s - amount ),
      l: hsl.l
    };
    return `#${ this.hslToHex( lessSaturatedHsl.h, lessSaturatedHsl.s, lessSaturatedHsl.l ) }`;
  }

  generateShades( startHex, endHex, numShades ) {
    startHex = startHex.replace( '#', '' );
    endHex = endHex.replace( '#', '' );

    const startR = parseInt( startHex.substring( 0, 2 ), 16 );
    const startG = parseInt( startHex.substring( 2, 4 ), 16 );
    const startB = parseInt( startHex.substring( 4, 6 ), 16 );

    const endR = parseInt( endHex.substring( 0, 2 ), 16 );
    const endG = parseInt( endHex.substring( 2, 4 ), 16 );
    const endB = parseInt( endHex.substring( 4, 6 ), 16 );

    const shades = [];
    numShades++;
    for ( let i = 0; i < numShades - 1; i++ ) {
      const newR = Math.round(
        startR - ( ( startR - endR ) / ( numShades - 1 ) ) * i
      );
      const newG = Math.round(
        startG - ( ( startG - endG ) / ( numShades - 1 ) ) * i
      );
      const newB = Math.round(
        startB - ( ( startB - endB ) / ( numShades - 1 ) ) * i
      );

      const newHex = `${ ( newR << 16 | newG << 8 | newB )
      .toString( 16 )
      .padStart( 6, '0' )
      .toLowerCase() }`;
      shades.push( '#' + newHex );
    }

    return shades;
  }

  generateWarmerColors( hex: string, numColors: number ): string[] {
    if ( numColors < 2 ) {
      throw new Error( 'Number of colors must be at least 2' );
    }

    const hsl = this.hexToHsl( hex.replace( '#', '' ) );
    const colors = [];

    const targetHue = 0; // Target hue for red

    // Calculate the shortest direction to red
    let hueDifference = hsl.h - targetHue;

    let clockwise = -1;

    if ( hueDifference > 180 ) {
      hueDifference = 360 - hueDifference;
      clockwise = 1;
    }

    const stepSize = Math.min( 9, ( hueDifference * clockwise ) / 12 );

    // Generate warmer colors by moving the hue towards red in the shortest direction
    for ( let i = 0; i < numColors; i++ ) {
      const newHue = ( hsl.h + i * stepSize + 360 ) % 360; // Ensure hue stays within 0-359
      const color = '#' + this.hslToHex( newHue, hsl.s, hsl.l );
      colors.push( color );
    }

    return colors;
  }

  generateCoolerColors( hex: string, numColors: number ): string[] {
    if ( numColors < 2 ) {
      throw new Error( 'Number of colors must be at least 2' );
    }

    const hsl = this.hexToHsl( hex.replace( '#', '' ) );
    const colors = [];

    const targetHue = 180; // Target hue for cyan (coolest color)

    // Calculate the shortest direction to cyan
    let hueDifference = targetHue - hsl.h;

    const stepSize = Math.min( 9, hueDifference / 12 );

    // Generate cooler colors by moving the hue towards cyan in the shortest direction
    for ( let i = 0; i < numColors; i++ ) {
      const newHue = ( hsl.h + i * stepSize + 360 ) % 360; // Ensure hue stays within 0-359
      const color = '#' + this.hslToHex( newHue, hsl.s, hsl.l );
      colors.push( color );
    }

    return colors;
  }

  generateMonochromeColors( hex: string, numColors: number, reverse = false ): string[] {
    if ( numColors < 2 ) {
      throw new Error( 'Number of colors must be at least 2' );
    }

    const hsl = this.hexToHsl( hex.replace( '#', '' ) );

    let step = Math.min( 8, hsl.l / ( numColors / 2 ) );

    const colors = [];

    numColors--;

    const end = numColors % 2 === 1 ? ( numColors / 2 ) - 1 : numColors / 2;

    for ( let i = 0; i < end; i++ ) {
      const newLightness = hsl.l - step * ( i + 1 );
      const color = '#' + this.hslToHex( hsl.h, hsl.s, Math.max( newLightness, 0 ) );
      colors.push( color );
    }

    colors.reverse();

    // Normalise the seed: getHexCode() returns the hex WITHOUT a '#', and pushing
    // it raw drops an invalid (black-rendering) colour into the middle of the ramp.
    colors.push( '#' + hex.replace( '#', '' ).toLowerCase() );

    step = Math.min( 8, ( 100 - hsl.l ) / ( numColors / 2 ) );

    for ( let i = 0; i < numColors / 2; i++ ) {
      const newLightness = hsl.l + step * ( i + 1 );
      const color = '#' + this.hslToHex( hsl.h, hsl.s, Math.min( newLightness, 100 ) );
      colors.push( color );
    }


    return reverse === true ? colors.reverse() : colors;
  }

  generateAnalogousColors( hex: string, numColors: number, reverse = false ): string[] {
    const hsl = this.hexToHsl( hex.replace( '#', '' ) );

    // Wider hue step so consecutive analogous colours read as distinct.
    const step = 11;

    const startHue = hsl.h;

    const colors = [];

    numColors--;

    const end = numColors % 2 === 1 ? ( numColors / 2 ) - 1 : numColors / 2;

    for ( let i = 0; i < end; i++ ) {
      const hue = ( startHue - ( ( i + 1 ) * step ) + 360 ) % 360;  // Ensure hue is within [0, 360)
      const color = '#' + this.hslToHex( hue, hsl.s, hsl.l );
      colors.push( color );
    }

    colors.reverse();

    // Normalise the seed: getHexCode() returns the hex WITHOUT a '#', and pushing
    // it raw drops an invalid (black-rendering) colour into the middle of the ramp.
    colors.push( '#' + hex.replace( '#', '' ).toLowerCase() );

    for ( let i = 0; i < numColors / 2; i++ ) {
      const hue = ( startHue + ( ( i + 1 ) * step ) + 360 ) % 360;  // Ensure hue is within [0, 360)
      const color = '#' + this.hslToHex( hue, hsl.s, hsl.l );
      colors.push( color );
    }

    return reverse === true ? colors.reverse() : colors;
  }

  /**
   * An N-step blend that walks across a harmony's member colours on the wheel:
   * hue advances forward from each member to the next, with saturation and
   * lightness interpolated linearly between them. The first and last members are
   * the endpoints, so e.g. a 2-colour complementary input yields an 11-step ramp
   * from the colour to its opposite, and a 3-colour triadic input ramps across
   * all three hues. Used to render each scheme as a single 11-swatch strip.
   */
  generateSchemeBlend( hexes: string[], steps = 11 ): string[] {
    const stops = ( hexes || [] )
      .filter( ( h ) => !!h )
      .map( ( h ) => this.hexToHsl( h.replace( '#', '' ) ) );
    if ( stops.length === 0 ) {
      return [];
    }
    if ( stops.length === 1 ) {
      const only = '#' + this.hslToHex( stops[ 0 ].h, stops[ 0 ].s, stops[ 0 ].l );
      return Array.from( { length: steps }, () => only );
    }
    const segments = stops.length - 1;
    const out: string[] = [];
    for ( let i = 0; i < steps; i++ ) {
      const p = ( i / ( steps - 1 ) ) * segments;          // 0 .. segments
      const seg = Math.min( Math.floor( p ), segments - 1 );
      const frac = p - seg;
      const a = stops[ seg ];
      const b = stops[ seg + 1 ];
      const dh = ( b.h - a.h + 360 ) % 360;                 // forward hue distance
      const h = ( a.h + dh * frac + 360 ) % 360;
      const s = a.s + ( b.s - a.s ) * frac;
      const l = a.l + ( b.l - a.l ) * frac;
      out.push( '#' + this.hslToHex( h, s, l ) );
    }
    return out;
  }

  generateColorBlindnessColors( hex ) {
    const colors = [];

    colors.push( {
      header: 'Protanomaly',
      value: '#' + blinder.protanomaly( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Deuteranomaly',
      value: '#' + blinder.deuteranomaly( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Tritanomaly',
      value: '#' + blinder.tritanomaly( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Achromatomaly',
      value: '#' + blinder.achromatomaly( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Protanopia',
      value: '#' + blinder.protanopia( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Deuteranopia',
      value: '#' + blinder.deuteranopia( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Tritanopia',
      value: '#' + blinder.tritanopia( hex ).substring( 1 ),
    } );
    colors.push( {
      header: 'Achromatopsia',
      value: '#' + blinder.achromatopsia( hex ).substring( 1 ),
    } );

    return colors;
  }

  generateColorBlindnessColorsForPalette( palette, type ) {
    const colors = [];

    palette.forEach( color => {
      colors.push( blinder[ type ]( color.value ) );
    } );

    return colors;
  }

  generateComplementaryColors( hex: string, numberOfColors: number ): string[] {
    const [ color1, color2 ] = this.calculateComplementaryColor( hex );
    const palette = [ color1 ];

    for ( let i = 1; i < numberOfColors - 1; i++ ) {
      const ratio = i / ( numberOfColors - 1 );
      const interpolatedColor = this.interpolateColors( color1, color2, ratio );
      palette.push( interpolatedColor );
    }

    palette.push( color2 );

    return palette;
  }

  generateSplitComplementaryColors( hex: string, numberOfColors: number ): string[] {
    const [ color1, color2, color3 ] = this.calculateSplitComplementaryColors( hex );
    const palette = [];

    for ( let i = 1; i < numberOfColors / 2 - 1; i++ ) {
      const ratio = i / ( numberOfColors / 2 - 1 );
      const interpolatedColor1 = this.interpolateColors( color1, color2, ratio );
      palette.push( interpolatedColor1 );
    }
    palette.push( color2 );

    palette.reverse();

    palette.push( color1 );

    for ( let i = 1; i < numberOfColors / 2; i++ ) {
      const ratio = i / ( numberOfColors / 2 );
      const interpolatedColor2 = this.interpolateColors( color1, color3, ratio );
      palette.push( interpolatedColor2 );
    }

    palette.push( color3 );

    return palette;
  }

  generateTriadicColors( hex: string, numberOfColors: number ): string[] {
    const [ color1, color2, color3 ] = this.calculateTriadicColors( hex );
    const palette = [];

    for ( let i = 1; i < numberOfColors / 2 - 1; i++ ) {
      const ratio = i / ( numberOfColors / 2 - 1 );
      const interpolatedColor1 = this.interpolateColors( color1, color2, ratio );
      palette.push( interpolatedColor1 );
    }
    palette.push( color2 );

    palette.reverse();

    palette.push( color1 );

    for ( let i = 1; i < numberOfColors / 2; i++ ) {
      const ratio = i / ( numberOfColors / 2 );
      const interpolatedColor2 = this.interpolateColors( color1, color3, ratio );
      palette.push( interpolatedColor2 );
    }

    palette.push( color3 );

    return palette;
  }

  generateTetradicColors( hex: string, numberOfColors: number ): string[] {
    const [ color1, color2, color3, color4 ] = this.calculateTetradicColors( hex );
    const palette = [];

    for ( let i = 1; i < numberOfColors / 4 - 1; i++ ) {
      const ratio = i / ( numberOfColors / 4 - 1 );
      const interpolatedColor = this.interpolateColors( color1, color2, ratio );
      palette.push( interpolatedColor );
    }

    palette.push( color2 );

    palette.reverse();

    palette.push( color1 );

    for ( let i = 1; i < numberOfColors / 4; i++ ) {
      const ratio = i / ( numberOfColors / 4 );
      const interpolatedColor = this.interpolateColors( color1, color4, ratio );
      palette.push( interpolatedColor );
    }

    palette.push( color4 );

    for ( let i = 1; i < numberOfColors / 4; i++ ) {
      const ratio = i / ( numberOfColors / 4 );
      const interpolatedColor = this.interpolateColors( color4, color3, ratio );
      palette.push( interpolatedColor );
    }

    palette.push( color3 );

    for ( let i = 1; i < numberOfColors / 4; i++ ) {
      const ratio = i / ( numberOfColors / 4 );
      const interpolatedColor = this.interpolateColors( color3, color2, ratio );
      palette.push( interpolatedColor );
    }

    return palette;
  }

  // Calculators

  calculateComplementaryColor( hex: string ): string[] {
    hex = hex.replace( '#', '' );

    // Convert hex to HSL
    const hsl = this.hexToHsl( hex );

    // Calculate complementary hue (opposite on the color wheel)
    const compHue = ( hsl.h + 180 ) % 360;

    // Convert the complementary HSL back to hex
    const compHex = this.hslToHex( compHue, hsl.s, hsl.l );

    return [ `#${ hex.toLowerCase() }`, `#${ compHex.toLowerCase() }` ];
  }

  calculateSplitComplementaryColors( hex: string ): string[] {
    hex = hex.replace( '#', '' );
    const hsl = this.hexToHsl( hex );

    // Calculate the split complementary hues
    const splitCompHue1 = ( hsl.h + 150 ) % 360;
    const splitCompHue2 = ( hsl.h + 210 ) % 360;

    // Convert the split complementary hues back to RGB
    const splitCompColor1 = '#' + this.hslToHex( splitCompHue1, hsl.s, hsl.l );
    const splitCompColor2 = '#' + this.hslToHex( splitCompHue2, hsl.s, hsl.l );

    return [ '#' + hex, splitCompColor1, splitCompColor2 ];
  }

  calculateTriadicColors( hex ) {
    hex = hex.replace( '#', '' );

    const hsl = this.hexToHsl( hex );

    // Calculate the triadic hues
    const triadicHue1 = ( hsl.h + 120 ) % 360;
    const triadicHue2 = ( hsl.h + 240 ) % 360;

    // Convert the triadic hues back to RGB
    const triadicColor1 = '#' + this.hslToHex( triadicHue1, hsl.s, hsl.l );
    const triadicColor2 = '#' + this.hslToHex( triadicHue2, hsl.s, hsl.l );

    return [ '#' + hex, triadicColor1, triadicColor2 ];
  }

  calculateTetradicColors( hex: string ): string[] {
    hex = hex.replace( '#', '' );
    const hsl = this.hexToHsl( hex );

    // Tetradic (rectangle): two complementary pairs forming a rectangle, so the
    // hues are offset +60 / +180 / +240 (gaps alternate 60 and 120 degrees). This
    // is distinct from a square scheme, which spaces all four hues evenly at 90.
    const tetradicHue1 = ( hsl.h + 60 ) % 360;
    const tetradicHue2 = ( hsl.h + 180 ) % 360;
    const tetradicHue3 = ( hsl.h + 240 ) % 360;

    // Convert the tetradic hues back to RGB
    const tetradicColor1 = '#' + this.hslToHex( tetradicHue1, hsl.s, hsl.l );
    const tetradicColor2 = '#' + this.hslToHex( tetradicHue2, hsl.s, hsl.l );
    const tetradicColor3 = '#' + this.hslToHex( tetradicHue3, hsl.s, hsl.l );

    return [ '#' + hex, tetradicColor1, tetradicColor2, tetradicColor3 ];
  }

  // Converters

  convertChartDataToRgbArray( chartData: ChartData ): [ number, number, number ] {
    const r = chartData.items.find( item => item.header === 'Red' )?.value ?? 0;
    const g = chartData.items.find( item => item.header === 'Green' )?.value ?? 0;
    const b = chartData.items.find( item => item.header === 'Blue' )?.value ?? 0;
    return [ r, g, b ];
  }

  hexToHsl( hex ) {
    // Remove the hash at the start if it's there
    hex = hex.replace( /^#/, '' );

    // Parse r, g, b values
    let r = parseInt( hex.substring( 0, 2 ), 16 ) / 255;
    let g = parseInt( hex.substring( 2, 4 ), 16 ) / 255;
    let b = parseInt( hex.substring( 4, 6 ), 16 ) / 255;

    // Find greatest and smallest channel values
    let cmin = Math.min( r, g, b ),
      cmax = Math.max( r, g, b ),
      delta = cmax - cmin,
      h = 0,
      s = 0,
      l = 0;

    // Calculate hue
    if ( delta == 0 )
      h = 0;
    else if ( cmax == r )
      h = ( ( g - b ) / delta ) % 6;
    else if ( cmax == g )
      h = ( b - r ) / delta + 2;
    else
      h = ( r - g ) / delta + 4;

    h = Math.round( h * 60 );

    // Make negative hues positive behind 360°
    if ( h < 0 )
      h += 360;

    // Calculate lightness
    l = ( cmax + cmin ) / 2;

    // Calculate saturation
    s = delta == 0 ? 0 : delta / ( 1 - Math.abs( 2 * l - 1 ) );

    // Multiply l and s by 100
    s = +( s * 100 ).toFixed( 1 );
    l = +( l * 100 ).toFixed( 1 );

    return { h, s, l };
  }

  /** hexToHsl with integer channels (h deg, s%, l%), rounded from the raw
   *  values (NOT from the one-decimal hexToHsl output, which would double-round
   *  e.g. 66.47 -> 66.5 -> 67). Matches the server-side rgbToHsl in
   *  seo-route-meta.js so converter URLs and titles agree everywhere. */
  hexToHslInts( hex: string ): { h: number; s: number; l: number } {
    hex = hex.replace( /^#/, '' );
    const r = parseInt( hex.substring( 0, 2 ), 16 ) / 255;
    const g = parseInt( hex.substring( 2, 4 ), 16 ) / 255;
    const b = parseInt( hex.substring( 4, 6 ), 16 ) / 255;
    const cmax = Math.max( r, g, b );
    const cmin = Math.min( r, g, b );
    const delta = cmax - cmin;
    let h = 0;
    if ( delta !== 0 ) {
      if ( cmax === r ) { h = ( ( g - b ) / delta ) % 6; }
      else if ( cmax === g ) { h = ( b - r ) / delta + 2; }
      else { h = ( r - g ) / delta + 4; }
      h = Math.round( h * 60 );
      if ( h < 0 ) { h += 360; }
    }
    const l = ( cmax + cmin ) / 2;
    const s = delta === 0 ? 0 : delta / ( 1 - Math.abs( 2 * l - 1 ) );
    return { h, s: Math.round( s * 100 ), l: Math.round( l * 100 ) };
  }

  hslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;

    const k = ( n: number ) => ( n + h / 30 ) % 12;
    const a = s * Math.min( l, 1 - l );
    const f = ( n: number ) => l - a * Math.max( Math.min( k( n ) - 3, 9 - k( n ), 1 ), -1 );

    const r = Math.round( f( 0 ) * 255 ).toString( 16 ).padStart( 2, '0' ).toLowerCase();
    const g = Math.round( f( 8 ) * 255 ).toString( 16 ).padStart( 2, '0' ).toLowerCase();
    const b = Math.round( f( 4 ) * 255 ).toString( 16 ).padStart( 2, '0' ).toLowerCase();

    return `${ r }${ g }${ b }`;
  }

  // Returns a translucent version of any CSS colour (hex or hsl()) at the given
  // alpha. Used for the faint channel-track wash on the colour tool.
  toRgba( color: string, alpha: number ): string {
    const c = ( color || '' ).trim();
    if ( c.startsWith( '#' ) ) {
      let hex = c.replace( '#', '' );
      if ( hex.length === 3 ) {
        hex = hex.split( '' ).map( ch => ch + ch ).join( '' );
      }
      const num = parseInt( hex, 16 );
      const r = ( num >> 16 ) & 255;
      const g = ( num >> 8 ) & 255;
      const b = num & 255;
      return `rgba(${ r }, ${ g }, ${ b }, ${ alpha })`;
    }
    if ( c.startsWith( 'hsl(' ) ) {
      return c.replace( /^hsl\(/, 'hsla(' ).replace( /\)$/, `, ${ alpha })` );
    }
    return c;
  }

  hexToRgb( hex ) {
    hex = hex.replace( '#', '' );

    // Convert hex to RGB
    const num = parseInt( hex, 16 );
    const red = ( num >> 16 ) & 255;
    const green = ( num >> 8 ) & 255;
    const blue = num & 255;

    // Return an array with the red, green, and blue values
    return {
      items: [
        { header: 'Red', value: red, transient: 'rgba(255,0,0,0.06)' },
        { header: 'Green', value: green, transient: 'rgba(0,255,0,0.06)' },
        { header: 'Blue', value: blue, transient: 'rgba(0,0,255,0.06)' },
      ],
      total: red + green + blue,
    };
  }

  hexToCmyk( hex ) {
    hex = hex.replace( '#', '' );

    // Convert the hex string to an integer
    const num = parseInt( hex, 16 );

    // Extract the red, green, and blue components
    const red = ( num >> 16 ) & 255;
    const green = ( num >> 8 ) & 255;
    const blue = num & 255;

    // Convert RGB to CMY
    let cyan = 1 - red / 255;
    let magenta = 1 - green / 255;
    let yellow = 1 - blue / 255;

    // Calculate black (K)
    let k = Math.min( cyan, magenta, yellow );
    if ( k === 1 ) {
      cyan = 0;
      magenta = 0;
      yellow = 0;
      k = 100;
    } else {
      cyan = Math.round( ( ( cyan - k ) / ( 1 - k ) ) * 100 );
      magenta = Math.round( ( ( magenta - k ) / ( 1 - k ) ) * 100 );
      yellow = Math.round( ( ( yellow - k ) / ( 1 - k ) ) * 100 );
      k = Math.round( k * 100 );
    }

    // Return an array with the CMYK values
    return {
      items: [
        { header: 'Cyan', value: cyan, transient: {} },
        { header: 'Magenta', value: magenta, transient: {} },
        { header: 'Yellow', value: yellow, transient: {} },
        { header: 'Black', value: k, transient: { alias: 'K' } },
      ],
      total: cyan + magenta + yellow + k,
    };
  }

  /** HSV (h 0-360, s/v 0-100) back to a hex string WITHOUT the hash, the
   *  mirror of hexToHsv (and the hsv sibling of hslToHex). */
  hsvToHex( h: number, s: number, v: number ): string {
    const hue = ( ( h % 360 ) + 360 ) % 360;
    const sN = Math.max( 0, Math.min( 100, s ) ) / 100;
    const vN = Math.max( 0, Math.min( 100, v ) ) / 100;
    const c = vN * sN;
    const x = c * ( 1 - Math.abs( ( hue / 60 ) % 2 - 1 ) );
    const m = vN - c;
    let r1 = 0, g1 = 0, b1 = 0;
    if ( hue < 60 ) { r1 = c; g1 = x; } else if ( hue < 120 ) { r1 = x; g1 = c; }
    else if ( hue < 180 ) { g1 = c; b1 = x; } else if ( hue < 240 ) { g1 = x; b1 = c; }
    else if ( hue < 300 ) { r1 = x; b1 = c; } else { r1 = c; b1 = x; }
    const toPair = ( n: number ) => Math.round( ( n + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
    return `${ toPair( r1 ) }${ toPair( g1 ) }${ toPair( b1 ) }`;
  }

  hexToHsv( hex: string ) {
    hex = hex.replace( '#', '' );

    // Convert hex to RGB
    const r = parseInt( hex.substring( 0, 2 ), 16 ) / 255;
    const g = parseInt( hex.substring( 2, 4 ), 16 ) / 255;
    const b = parseInt( hex.substring( 4, 6 ), 16 ) / 255;

    const max = Math.max( r, g, b );
    const min = Math.min( r, g, b );
    let h, s, v = max;

    const d = max - min;
    s = max === 0 ? 0 : d / max;

    if ( max === min ) {
      h = 0; // achromatic
    } else {
      switch ( max ) {
        case r:
          h = ( g - b ) / d + ( g < b ? 6 : 0 );
          break;
        case g:
          h = ( b - r ) / d + 2;
          break;
        case b:
          h = ( r - g ) / d + 4;
          break;
      }
      h /= 6;
    }

    return [ Math.round( h * 360 ), Math.round( s * 100 ), Math.round( v * 100 ) ];
  }

  hexToXyz( hex: string ) {
    hex = hex.replace( '#', '' );

    // Convert hex to RGB
    const r = parseInt( hex.substring( 0, 2 ), 16 ) / 255;
    const g = parseInt( hex.substring( 2, 4 ), 16 ) / 255;
    const b = parseInt( hex.substring( 4, 6 ), 16 ) / 255;

    // Apply gamma correction
    const rgb = [ r, g, b ].map( ( val ) => {
      if ( val <= 0.03928 ) {
        return val / 12.92;
      } else {
        return Math.pow( ( val + 0.055 ) / 1.055, 2.4 );
      }
    } );

    // Convert RGB to XYZ
    const x = rgb[ 0 ] * 0.4124564 + rgb[ 1 ] * 0.3575761 + rgb[ 2 ] * 0.1804375;
    const y = rgb[ 0 ] * 0.2126729 + rgb[ 1 ] * 0.7151522 + rgb[ 2 ] * 0.0721750;
    const z = rgb[ 0 ] * 0.0193339 + rgb[ 1 ] * 0.1191920 + rgb[ 2 ] * 0.9503041;

    return [ x, y, z ];
  }

  hexToLab( hex: string ): [ number, number, number ] {
    hex = hex.replace( '#', '' );

    // Convert hex to RGB
    const r = parseInt( hex.substring( 0, 2 ), 16 ) / 255;
    const g = parseInt( hex.substring( 2, 4 ), 16 ) / 255;
    const b = parseInt( hex.substring( 4, 6 ), 16 ) / 255;

    // Apply gamma correction
    const rgb = [ r, g, b ].map( ( val ) => {
      if ( val <= 0.03928 ) {
        return val / 12.92;
      } else {
        return Math.pow( ( val + 0.055 ) / 1.055, 2.4 );
      }
    } );

    // Convert RGB to XYZ
    const x = rgb[ 0 ] * 0.4124564 + rgb[ 1 ] * 0.3575761 + rgb[ 2 ] * 0.1804375;
    const y = rgb[ 0 ] * 0.2126729 + rgb[ 1 ] * 0.7151522 + rgb[ 2 ] * 0.0721750;
    const z = rgb[ 0 ] * 0.0193339 + rgb[ 1 ] * 0.1191920 + rgb[ 2 ] * 0.9503041;

    // Normalize XYZ to D65 white point
    const refX = 0.95047;
    const refY = 1.00000;
    const refZ = 1.08883;
    const xNorm = x / refX;
    const yNorm = y / refY;
    const zNorm = z / refZ;

    // Convert XYZ to LAB
    const fx = xNorm > 0.008856 ? Math.pow( xNorm, 1 / 3 ) : ( 903.3 * xNorm + 16 ) / 116;
    const fy = yNorm > 0.008856 ? Math.pow( yNorm, 1 / 3 ) : ( 903.3 * yNorm + 16 ) / 116;
    const fz = zNorm > 0.008856 ? Math.pow( zNorm, 1 / 3 ) : ( 903.3 * zNorm + 16 ) / 116;

    const l = ( 116 * fy ) - 16;
    const a = 500 * ( fx - fy );
    const bValue = 200 * ( fy - fz );

    return [ Math.round( l * 1000 ) / 1000, Math.round( a * 1000 ) / 1000, Math.round( bValue * 1000 ) / 1000 ];
  }

  /** CIELAB (L 0..100, signed a/b ~±128) -> 6-digit hex (no '#'), clamped to the
   *  sRGB gamut. Inverse of hexToLab: CIELAB -> XYZ (D65) -> linear sRGB -> gamma. */
  labToHex( l: number, a: number, b: number ): string {
    const fy = ( l + 16 ) / 116;
    const fx = a / 500 + fy;
    const fz = fy - b / 200;
    const eps = 0.008856, kappa = 903.3;
    const fx3 = fx * fx * fx, fz3 = fz * fz * fz;
    const xr = fx3 > eps ? fx3 : ( 116 * fx - 16 ) / kappa;
    const yr = l > kappa * eps ? fy * fy * fy : l / kappa;
    const zr = fz3 > eps ? fz3 : ( 116 * fz - 16 ) / kappa;
    const X = xr * 0.95047, Y = yr * 1.00000, Z = zr * 1.08883;
    const rLin = X * 3.2404542 + Y * -1.5371385 + Z * -0.4985314;
    const gLin = X * -0.9692660 + Y * 1.8760108 + Z * 0.0415560;
    const bLin = X * 0.0556434 + Y * -0.2040259 + Z * 1.0572252;
    const enc = ( v: number ): string => {
      const lin = Math.max( 0, Math.min( 1, v ) );
      const srgb = lin <= 0.0031308 ? 12.92 * lin : 1.055 * Math.pow( lin, 1 / 2.4 ) - 0.055;
      return Math.round( Math.max( 0, Math.min( 1, srgb ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return enc( rLin ) + enc( gLin ) + enc( bLin );
  }

  /** CIE XYZ (D65, 0..1 scale) -> 6-digit hex (no '#'), clamped to the sRGB
   *  gamut. The shared tail of every XYZ-based inverse (Lab, Yxy, Hunter Lab). */
  xyzToHex( X: number, Y: number, Z: number ): string {
    const rLin = X * 3.2404542 + Y * -1.5371385 + Z * -0.4985314;
    const gLin = X * -0.9692660 + Y * 1.8760108 + Z * 0.0415560;
    const bLin = X * 0.0556434 + Y * -0.2040259 + Z * 1.0572252;
    const enc = ( v: number ): string => {
      const lin = Math.max( 0, Math.min( 1, v ) );
      const srgb = lin <= 0.0031308 ? 12.92 * lin : 1.055 * Math.pow( lin, 1 / 2.4 ) - 0.055;
      return Math.round( Math.max( 0, Math.min( 1, srgb ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return enc( rLin ) + enc( gLin ) + enc( bLin );
  }

  /** CIE xyY (Yxy: Y 0..100, chromaticity x, y) -> hex. Inverse of hexToYxy:
   *  X = x·Y/y, Z = (1 - x - y)·Y/y, then the shared XYZ tail. y = 0 is black. */
  yxyToHex( Y: number, x: number, y: number ): string {
    if ( y <= 0 || Y <= 0 ) {
      return '000000';
    }
    const Yn = Y / 100;
    return this.xyzToHex( ( x * Yn ) / y, Yn, ( ( 1 - x - y ) * Yn ) / y );
  }

  /** Hunter Lab (1958 formulation) -> hex. Inverse of hexToHunterLab:
   *  Y = (L/10)², X = (a·√Y/17.5 + Y)/1.02, Z = (Y - b·√Y/7)/0.847, XYZ 0..100. */
  hunterLabToHex( l: number, a: number, b: number ): string {
    if ( l <= 0 ) {
      return '000000';
    }
    const sq = l / 10;
    const Y = sq * sq;
    const X = ( ( a * sq ) / 17.5 + Y ) / 1.02;
    const Z = ( Y - ( b * sq ) / 7 ) / 0.847;
    return this.xyzToHex( X / 100, Y / 100, Z / 100 );
  }

  /** CIELUV (CIE 1976 L*u*v*, D65) -> hex, clamped to the sRGB gamut. Inverse of
   *  hexToLuv: Y from L (the CIELAB lightness curve), u′ = u/(13L) + u′n,
   *  v′ = v/(13L) + v′n, X = Y·9u′/(4v′), Z = Y·(12 − 3u′ − 20v′)/(4v′). */
  luvToHex( l: number, u: number, v: number ): string {
    if ( l <= 0 ) {
      return '000000';
    }
    const Xn = 0.95047, Yn = 1.00000, Zn = 1.08883;
    const dn = Xn + 15 * Yn + 3 * Zn;
    const un = ( 4 * Xn ) / dn, vn = ( 9 * Yn ) / dn;
    const eps = 0.008856, kappa = 903.3;
    const Y = Yn * ( l > kappa * eps ? Math.pow( ( l + 16 ) / 116, 3 ) : l / kappa );
    const up = u / ( 13 * l ) + un;
    const vp = v / ( 13 * l ) + vn;
    if ( vp <= 0 ) {
      return '000000';
    }
    const X = Y * ( 9 * up ) / ( 4 * vp );
    const Z = Y * ( 12 - 3 * up - 20 * vp ) / ( 4 * vp );
    return this.xyzToHex( X, Y, Z );
  }

  /** Display-P3 channels [0..1] -> hex, clamped to the sRGB gamut. Inverse of
   *  hexToDisplayP3: decode the P3 transfer curve, map linear P3 into linear
   *  sRGB (inverse of the Bradford-adapted matrix), re-encode as sRGB. */
  displayP3ToHex( r: number, g: number, b: number ): string {
    const dec = ( c: number ): number => {
      const v = Math.max( 0, Math.min( 1, c ) );
      return v <= 0.04045 ? v / 12.92 : Math.pow( ( v + 0.055 ) / 1.055, 2.4 );
    };
    const [ pr, pg, pb ] = [ dec( r ), dec( g ), dec( b ) ];
    const rLin = 1.2249401 * pr - 0.2249404 * pg;
    const gLin = -0.0420569 * pr + 1.0420571 * pg;
    const bLin = -0.0196376 * pr - 0.0786361 * pg + 1.0982735 * pb;
    const enc = ( v: number ): string => {
      const lin = Math.max( 0, Math.min( 1, v ) );
      const srgb = lin <= 0.0031308 ? 12.92 * lin : 1.055 * Math.pow( lin, 1 / 2.4 ) - 0.055;
      return Math.round( Math.max( 0, Math.min( 1, srgb ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return enc( rLin ) + enc( gLin ) + enc( bLin );
  }

  /** CIE xyY (Yxy): luminance Y (0..100) plus chromaticity coordinates x, y. */
  hexToYxy( hex: string ): [ number, number, number ] {
    const [ x, y, z ] = this.hexToXyz( hex ).map( ( n: number ) => n * 100 );
    const sum = x + y + z || 1;
    return [
      Math.round( y * 100 ) / 100,
      Math.round( ( x / sum ) * 10000 ) / 10000,
      Math.round( ( y / sum ) * 10000 ) / 10000,
    ];
  }

  /** Hunter Lab (1958 formulation), derived from D65 XYZ scaled to 0..100. */
  hexToHunterLab( hex: string ): [ number, number, number ] {
    const [ X, Y, Z ] = this.hexToXyz( hex ).map( ( n: number ) => n * 100 );
    if ( Y <= 0 ) {
      return [ 0, 0, 0 ];
    }
    const sq = Math.sqrt( Y );
    const l = 10 * sq;
    const a = 17.5 * ( ( 1.02 * X - Y ) / sq );
    const b = 7 * ( ( Y - 0.847 * Z ) / sq );
    return [
      Math.round( l * 100 ) / 100,
      Math.round( a * 100 ) / 100,
      Math.round( b * 100 ) / 100,
    ];
  }

  /** CIELUV (CIE 1976 L*u*v*), derived from D65 XYZ: L on the CIELAB lightness
   *  curve, u and v from the u′v′ chromaticity distance to the white point. */
  hexToLuv( hex: string ): [ number, number, number ] {
    const [ X, Y, Z ] = this.hexToXyz( hex );
    const Xn = 0.95047, Yn = 1.00000, Zn = 1.08883;
    const yr = Y / Yn;
    const eps = 0.008856, kappa = 903.3;
    const l = yr > eps ? 116 * Math.cbrt( yr ) - 16 : kappa * yr;
    const d = X + 15 * Y + 3 * Z;
    if ( d <= 0 || l <= 0 ) {
      return [ 0, 0, 0 ];
    }
    const dn = Xn + 15 * Yn + 3 * Zn;
    const u = 13 * l * ( ( 4 * X ) / d - ( 4 * Xn ) / dn );
    const v = 13 * l * ( ( 9 * Y ) / d - ( 9 * Yn ) / dn );
    const r2 = ( n: number ): number => Math.round( n * 100 ) / 100 || 0;
    return [ r2( l ), r2( u ), r2( v ) ];
  }

  /** APCA screen luminance (Y) from a hex, simple 2.4 power curve per APCA. */
  private apcaY( hex: string ): number {
    const h = ( hex || '' ).replace( '#', '' );
    const r = Math.pow( parseInt( h.substring( 0, 2 ), 16 ) / 255, 2.4 );
    const g = Math.pow( parseInt( h.substring( 2, 4 ), 16 ) / 255, 2.4 );
    const b = Math.pow( parseInt( h.substring( 4, 6 ), 16 ) / 255, 2.4 );
    return 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;
  }

  /**
   * APCA (WCAG 3 draft) lightness contrast Lc for text over a background.
   * Returns a signed value (~ -108..106); magnitude is what thresholds use.
   * Implements the APCA 0.0.98 formula.
   */
  apcaContrast( textHex: string, bgHex: string ): number {
    const blkThrs = 0.022, blkClmp = 1.414;
    let txtY = this.apcaY( textHex );
    let bgY = this.apcaY( bgHex );
    txtY = txtY > blkThrs ? txtY : txtY + Math.pow( blkThrs - txtY, blkClmp );
    bgY = bgY > blkThrs ? bgY : bgY + Math.pow( blkThrs - bgY, blkClmp );
    if ( Math.abs( bgY - txtY ) < 0.0005 ) {
      return 0;
    }
    let sapc: number;
    let out: number;
    if ( bgY > txtY ) {
      sapc = ( Math.pow( bgY, 0.56 ) - Math.pow( txtY, 0.57 ) ) * 1.14;
      out = sapc < 0.1 ? 0 : sapc - 0.027;
    } else {
      sapc = ( Math.pow( bgY, 0.65 ) - Math.pow( txtY, 0.62 ) ) * 1.14;
      out = sapc > -0.1 ? 0 : sapc + 0.027;
    }
    return Math.round( out * 100 );
  }

  /** Plain-language APCA usage level for an Lc magnitude. */
  apcaLevel( lc: number ): string {
    const a = Math.abs( lc );
    if ( a >= 75 ) return 'Body text';
    if ( a >= 60 ) return 'Large / bold';
    if ( a >= 45 ) return 'Large text';
    if ( a >= 30 ) return 'UI / non-text';
    return 'Fail';
  }

  /** Per-channel octal (R G B), mirroring the per-channel binary/decimal bases. */
  hexToOctal( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return [ 0, 2, 4 ]
      .map( ( i ) => parseInt( h.substring( i, i + 2 ), 16 ).toString( 8 ) )
      .join( ' ' );
  }

  // --- Extra conversions + accessibility (color tool page) ------------------

  /** Linear-light sRGB channels [0..1] from a hex string (WCAG 2.x curve). */
  private linearRgb( hex: string ): [ number, number, number ] {
    const h = ( hex || '' ).replace( '#', '' );
    return [ 0, 2, 4 ].map( i => {
      const c = parseInt( h.substring( i, i + 2 ), 16 ) / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
    } ) as [ number, number, number ];
  }

  /**
   * Display-P3 channels [0..1] for the color (CSS `color(display-p3 r g b)`).
   * Linear sRGB is mapped into linear P3 with the standard Bradford-adapted
   * matrix, then re-encoded with the shared sRGB/P3 transfer curve. sRGB is a
   * subset of P3, so in-gamut inputs always land in [0, 1].
   */
  hexToDisplayP3( hex: string ): [ number, number, number ] {
    const [ r, g, b ] = this.linearRgb( hex );
    const lin: [ number, number, number ] = [
      0.8224621 * r + 0.1775380 * g,
      0.0331941 * r + 0.9668058 * g,
      0.0170827 * r + 0.0723974 * g + 0.9105199 * b,
    ];
    return lin.map( c => {
      const v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow( c, 1 / 2.4 ) - 0.055;
      return Math.round( Math.min( 1, Math.max( 0, v ) ) * 10000 ) / 10000;
    } ) as [ number, number, number ];
  }

  /** WCAG relative luminance in [0, 1]. */
  relativeLuminance( hex: string ): number {
    const [ r, g, b ] = this.linearRgb( hex );
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  /** WCAG contrast ratio between two colors, 1..21. */
  contrastRatio( hex1: string, hex2: string ): number {
    const l1 = this.relativeLuminance( hex1 );
    const l2 = this.relativeLuminance( hex2 );
    const light = Math.max( l1, l2 );
    const dark = Math.min( l1, l2 );
    return Math.round( ( ( light + 0.05 ) / ( dark + 0.05 ) ) * 100 ) / 100;
  }

  /** WCAG conformance for a ratio: 'AAA' | 'AA' | 'Fail' (large-text thresholds when largeText). */
  wcagLevel( ratio: number, largeText = false ): string {
    const aa = largeText ? 3 : 4.5;
    const aaa = largeText ? 4.5 : 7;
    if ( ratio >= aaa ) {
      return 'AAA';
    }
    if ( ratio >= aa ) {
      return 'AA';
    }
    return 'Fail';
  }

  /** HWB (hue, whiteness %, blackness %). */
  hexToHwb( hex: string ): [ number, number, number ] {
    const h = ( hex || '' ).replace( '#', '' );
    const r = parseInt( h.substring( 0, 2 ), 16 ) / 255;
    const g = parseInt( h.substring( 2, 4 ), 16 ) / 255;
    const b = parseInt( h.substring( 4, 6 ), 16 ) / 255;
    const hue = this.hexToHsl( hex ).h;
    const w = Math.round( Math.min( r, g, b ) * 100 );
    const bl = Math.round( ( 1 - Math.max( r, g, b ) ) * 100 );
    return [ hue, w, bl ];
  }

  /** CMYK (each 0-100) to a bare 6-digit hex: every channel is the paper's
   *  full brightness with its ink coverage and the shared key removed. */
  cmykToHex( c: number, m: number, y: number, k: number ): string {
    const n = ( v: number ) => Math.max( 0, Math.min( 100, v ) ) / 100;
    const kN = n( k );
    const toPair = ( ink: number ) =>
      Math.round( 255 * ( 1 - n( ink ) ) * ( 1 - kN ) ).toString( 16 ).padStart( 2, '0' );
    return toPair( c ) + toPair( m ) + toPair( y );
  }

  /** HWB (h 0-360, w/b 0-100) to a bare 6-digit hex. Whiteness lifts every
   *  channel's floor and blackness lowers the ceiling; when the two overflow
   *  together (w + b >= 100) the hue cancels out and the color is the grey
   *  w / (w + b), per the CSS hwb() normalization. */
  hwbToHex( h: number, w: number, b: number ): string {
    const wN = Math.max( 0, Math.min( 100, w ) ) / 100;
    const bN = Math.max( 0, Math.min( 100, b ) ) / 100;
    if ( wN + bN >= 1 ) {
      const grey = Math.round( ( wN / ( wN + bN ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
      return grey + grey + grey;
    }
    const pure = this.hsvToHex( h, 100, 100 );
    const scale = 1 - wN - bN;
    const toPair = ( i: number ) => {
      const channel = parseInt( pure.substring( i * 2, i * 2 + 2 ), 16 ) / 255;
      return Math.round( ( channel * scale + wN ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return toPair( 0 ) + toPair( 1 ) + toPair( 2 );
  }

  /** OKLAB (perceptual). L in [0,1], a/b small signed. */
  hexToOklab( hex: string ): [ number, number, number ] {
    const [ r, g, b ] = this.linearRgb( hex );
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    const l_ = Math.cbrt( l );
    const m_ = Math.cbrt( m );
    const s_ = Math.cbrt( s );
    const okL = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
    const okA = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
    const okB = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
    const round = ( n: number ) => Math.round( n * 1000 ) / 1000;
    return [ round( okL ), round( okA ), round( okB ) ];
  }

  /** OKLCH (L [0,1], chroma, hue degrees). */
  hexToOklch( hex: string ): [ number, number, number ] {
    const [ l, a, b ] = this.hexToOklab( hex );
    const c = Math.sqrt( a * a + b * b );
    let h = Math.atan2( b, a ) * 180 / Math.PI;
    if ( h < 0 ) {
      h += 360;
    }
    return [ l, Math.round( c * 1000 ) / 1000, Math.round( h * 10 ) / 10 ];
  }

  /** OKLCH (L in [0,1], chroma, hue degrees) -> 6-digit hex (no '#'), clamped to
   *  the sRGB gamut. Inverse of hexToOklch: polar OKLCH -> OKLAB -> linear sRGB
   *  (inverse matrix, cubed LMS) -> gamma-encoded sRGB. Out-of-gamut inputs clamp
   *  per channel (a simple, predictable fallback rather than full gamut mapping). */
  oklchToHex( l: number, c: number, h: number ): string {
    const hr = ( h * Math.PI ) / 180;
    const a = c * Math.cos( hr );
    const b = c * Math.sin( hr );
    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.2914855480 * b;
    const lc = l_ * l_ * l_;
    const mc = m_ * m_ * m_;
    const sc = s_ * s_ * s_;
    const rLin = 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
    const gLin = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
    const bLin = -0.0041960863 * lc - 0.7034186147 * mc + 1.7076147010 * sc;
    const encode = ( v: number ): string => {
      const lin = Math.max( 0, Math.min( 1, v ) );
      const srgb = lin <= 0.0031308 ? 12.92 * lin : 1.055 * Math.pow( lin, 1 / 2.4 ) - 0.055;
      return Math.round( Math.max( 0, Math.min( 1, srgb ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return encode( rLin ) + encode( gLin ) + encode( bLin );
  }

  /** OKLAB (L in [0,1], signed a/b) -> 6-digit hex (no '#'), clamped to the sRGB
   *  gamut. Inverse of hexToOklab: OKLAB -> linear sRGB (inverse matrix, cubed
   *  LMS) -> gamma-encoded sRGB. The polar sibling oklchToHex shares this tail. */
  oklabToHex( l: number, a: number, b: number ): string {
    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.2914855480 * b;
    const lc = l_ * l_ * l_;
    const mc = m_ * m_ * m_;
    const sc = s_ * s_ * s_;
    const rLin = 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
    const gLin = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
    const bLin = -0.0041960863 * lc - 0.7034186147 * mc + 1.7076147010 * sc;
    const encode = ( v: number ): string => {
      const lin = Math.max( 0, Math.min( 1, v ) );
      const srgb = lin <= 0.0031308 ? 12.92 * lin : 1.055 * Math.pow( lin, 1 / 2.4 ) - 0.055;
      return Math.round( Math.max( 0, Math.min( 1, srgb ) ) * 255 ).toString( 16 ).padStart( 2, '0' );
    };
    return encode( rLin ) + encode( gLin ) + encode( bLin );
  }

  /** CIELCH derived from CIELAB (L, chroma, hue degrees). */
  hexToLch( hex: string ): [ number, number, number ] {
    const [ l, a, b ] = this.hexToLab( hex );
    const c = Math.sqrt( a * a + b * b );
    let h = Math.atan2( b, a ) * 180 / Math.PI;
    if ( h < 0 ) {
      h += 360;
    }
    return [ Math.round( l * 100 ) / 100, Math.round( c * 100 ) / 100, Math.round( h * 10 ) / 10 ];
  }

  /** CIELCH (L 0..100, chroma, hue degrees) -> 6-digit hex (no '#'), clamped to
   *  the sRGB gamut. Inverse of hexToLch: polar CIELCH -> CIELAB -> hex. */
  lchToHex( l: number, c: number, h: number ): string {
    const hr = ( h * Math.PI ) / 180;
    return this.labToHex( l, c * Math.cos( hr ), c * Math.sin( hr ) );
  }

  /** Integer value of the color (e.g. 16711680 for #ff0000). */
  hexToDecimalInt( hex: string ): number {
    return parseInt( ( hex || '' ).replace( '#', '' ), 16 );
  }

  /** Space-separated 8-bit binary per channel. */
  hexToBinary( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return [ 0, 2, 4 ].map(
      i => parseInt( h.substring( i, i + 2 ), 16 ).toString( 2 ).padStart( 8, '0' )
    ).join( ' ' );
  }

  /** RGB as whole percentages. */
  rgbToPercent( hex: string ): [ number, number, number ] {
    const h = ( hex || '' ).replace( '#', '' );
    return [ 0, 2, 4 ].map(
      i => Math.round( ( parseInt( h.substring( i, i + 2 ), 16 ) / 255 ) * 100 )
    ) as [ number, number, number ];
  }

  /** Nearest web-safe color (each channel snapped to a 0/51/.../255 step). */
  nearestWebSafe( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    const snap = [ 0, 2, 4 ].map(
      i => Math.round( parseInt( h.substring( i, i + 2 ), 16 ) / 51 ) * 51
    );
    return this.rgbToHex( snap[ 0 ], snap[ 1 ], snap[ 2 ] );
  }

  // RAL Classic Lab values, computed once on first use and reused for every
  // subsequent nearestRal lookup (216 colours, so recomputing per call is waste).
  private ralLabCache: Array<{ ral: RalColor; lab: [ number, number, number ] }> | null = null;

  private ralLab(): Array<{ ral: RalColor; lab: [ number, number, number ] }> {
    if ( !this.ralLabCache ) {
      this.ralLabCache = RAL_CLASSIC.map( ral => ( { ral, lab: this.hexToLab( ral.hex ) } ) );
    }
    return this.ralLabCache;
  }

  // Closest RAL Classic colour to a hex, by perceptual distance (CIE76 deltaE in
  // Lab). RAL colours are physical paint samples, so this is always an
  // approximation: deltaE near 0 is a near-exact match, larger values are looser.
  nearestRal( hex: string ): RalMatch | null {
    if ( !this.isValidHexCode( ( hex || '' ).replace( '#', '' ) ) ) {
      return null;
    }
    const [ l1, a1, b1 ] = this.hexToLab( hex );
    let best: RalColor | null = null;
    let bestD = Infinity;
    for ( const { ral, lab } of this.ralLab() ) {
      const dl = l1 - lab[ 0 ], da = a1 - lab[ 1 ], db = b1 - lab[ 2 ];
      const d = dl * dl + da * da + db * db;
      if ( d < bestD ) { bestD = d; best = ral; }
    }
    if ( !best ) { return null; }
    return { ...best, deltaE: Math.round( Math.sqrt( bestD ) * 10 ) / 10 };
  }

  // The closest RAL Classic code as a display string, e.g. "RAL 5012". The
  // colour NAME is intentionally left off here (kept on the data + nearestRal for
  // future use); the conversions UI shows the code only.
  nearestRalCode( hex: string ): string {
    const m = this.nearestRal( hex );
    return m ? `RAL ${ m.code }` : '';
  }

  rgbToHsv( rgb ) {
    const r = rgb.items[ 0 ].value / 255;
    const g = rgb.items[ 1 ].value / 255;
    const b = rgb.items[ 2 ].value / 255;
    const max = Math.max( r, g, b ), min = Math.min( r, g, b );
    let h = 0, s = 0, v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;
    if ( max !== min ) {
      switch ( max ) {
        case r:
          h = ( g - b ) / d + ( g < b ? 6 : 0 );
          break;
        case g:
          h = ( b - r ) / d + 2;
          break;
        case b:
          h = ( r - g ) / d + 4;
          break;
      }
      h /= 6;
    }
    return { h, s, v };
  }

  // Sorters

  sortByHue( palette ) {
    palette.sort( ( a, b ) => {
      const hslA = this.hexToHsl( a.value );
      const hslB = this.hexToHsl( b.value );
      return hslA.h - hslB.h;
    } );
  }

  // Utils

  getColorTemperature( hex ): string {
    const hue = this.hexToHsl( hex ).h;
    // Assuming hue is a value between 0 and 360
    if ( ( hue >= 0 && hue < 90 ) || ( hue >= 270 && hue <= 360 ) ) {
      return 'warm';
    } else {
      return 'cool';
    }
  }

  interpolateColors( hex1: string, hex2: string, ratio: number ): string {
    const rgb1 = this.convertChartDataToRgbArray( this.hexToRgb( hex1 ) );
    const rgb2 = this.convertChartDataToRgbArray( this.hexToRgb( hex2 ) );
    const [ r1, g1, b1 ] = rgb1;
    const [ r2, g2, b2 ] = rgb2;
    const r = Math.round( r1 + ratio * ( r2 - r1 ) );
    const g = Math.round( g1 + ratio * ( g2 - g1 ) );
    const b = Math.round( b1 + ratio * ( b2 - b1 ) );
    return this.rgbToHex( r, g, b );
  }

  // Method to convert RGB to hex
  rgbToHex( r: number, g: number, b: number ): string {
    return `#${ ( ( 1 << 24 ) + ( r << 16 ) + ( g << 8 ) + b ).toString( 16 ).slice( 1 ).toLowerCase() }`;
  }

  isValidHexCode( inputColorText: string ) {
    return /^([A-Fa-f0-9]{6})$/.test( inputColorText );
  }

  getColorName( hexCode: string ): string {
    return COLOR_NAMES[ hexCode.toUpperCase() ] || '';
  }

  getHexCode( color: string ) {
    return HEX_CODES[ color ] || '';
  }

  // Curated name for a set of colours (a gradient or a palette), looked up in
  // GRADIENT_NAMES. The key is order-independent: hexes are '#'-stripped,
  // lowercased and sorted before joining, so the same colours always resolve to
  // the same name regardless of order. Returns '' when the set is not named.
  getColorSetName( hexes: string[] ): string {
    if ( !hexes || hexes.length < 2 ) {
      return '';
    }
    const key = hexes
      .map( h => h.replace( '#', '' ).toLowerCase() )
      .sort()
      .join( '-' );
    return GRADIENT_NAMES[ key ] || '';
  }

  getRandomNamedColor() {
    const keys = Object.keys( HEX_CODES );
    const randomIndex = Math.floor( Math.random() * keys.length );
    return keys[ randomIndex ];
  }

  isComplementary( hue1: number, hue2: number ): boolean {
    return Math.abs( hue1 - hue2 ) >= 0.45 && Math.abs( hue1 - hue2 ) <= 0.55;
  }

  isAnalogous( hue1: number, hue2: number ): boolean {
    hue1 = hue1 * 360;
    hue2 = hue2 * 360;

    // Ensure hues are within the range [0, 360)
    hue1 = hue1 % 360;
    hue2 = hue2 % 360;

    // Calculate the absolute difference considering the circular nature of the color wheel
    let diff = Math.abs( hue1 - hue2 );
    if ( diff > 180 ) {
      diff = 360 - diff;
    }

    // Adjust the threshold as needed, here it's set to 30 degrees as an example
    const threshold = 30;
    return diff <= threshold;
  }

  isTriadic( hues: number[] ): boolean {
    hues.sort();
    return Math.abs( hues[ 0 ] - hues[ 1 ] ) >= 0.3 && Math.abs( hues[ 1 ] - hues[ 2 ] ) >= 0.3 && Math.abs( hues[ 2 ] - hues[ 0 ] ) >= 0.3;
  }

  isWarm( hue: number ): boolean {
    hue = hue * 360;
    return ( hue >= 0 && hue <= 90 ) || ( hue >= 270 && hue <= 360 );
  }

  isCool( hue: number ): boolean {
    hue = hue * 360;
    return ( hue >= 90 && hue <= 270 );
  }
}
